/**
 * Trends: in-season shifts in what customers choose — frame finish, series and
 * size; cover line and colour — as data only. No statuses, colours that mean
 * "act", or alerts: the reader (or the AI assistant, via the `trends` tool)
 * decides what it means. Buying stays on To order; deliveries on Arriving.
 *
 * Numbers come from GET /api/trends; grouping and every comparison is
 * shared/trends.ts, the same code the AI tool uses.
 */
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  TREND_DIMENSIONS,
  trendTable,
  type LeadTimeSettingsResponse,
  type TrendBasis,
  type TrendRow,
  type TrendsResponse,
} from "@march/shared";
import { apiGet } from "../lib/api.ts";
import { CATEGORY_LABEL, isYards } from "../lib/categories.ts";
import { downloadCsv } from "../lib/csv.ts";
import { fmt, shortDate } from "../lib/supply.ts";
import { partName } from "../lib/skuNames.ts";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import { ExportButton, ListBar } from "../components/ListBar.tsx";

const TYPES = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"] as const;
type TType = (typeof TYPES)[number];
const SERIES_WEEKS = 13;
type Sort = "share" | "units" | "change";
const SORT_LABEL: Record<Sort, string> = { share: "Share moved", units: "Most units", change: "% change" };

/** Frames and covers default to customer choices (inside umbrellas); the rest are mostly sold on their own. */
const defaultBasis = (t: TType): TrendBasis => (t === "Frame" || t === "Cover" ? "umbrellas" : "all");

const signed = (n: number | null, digits = 0, unit = "%") =>
  n == null ? "—" : `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(digits)}${unit}`;
const pctOf = (x: number) => `${(x * 100).toFixed(1)}%`;

export function TrendsPage() {
  const [type, setType] = useState<TType>("Frame");
  const dims = TREND_DIMENSIONS[type];
  const [dimByType, setDimByType] = useState<Partial<Record<TType, string>>>({});
  const dim = dimByType[type] ?? dims[0].key;
  const [basisByType, setBasisByType] = useState<Partial<Record<TType, TrendBasis>>>({});
  const basis = basisByType[type] ?? defaultBasis(type);
  const [win, setWin] = useState<4 | 8>(4);
  const [sort, setSort] = useState<Sort>("share");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery<TrendsResponse>({
    queryKey: ["trends", type, SERIES_WEEKS],
    queryFn: () => apiGet(`/api/trends?category=${type}&weeks=${SERIES_WEEKS}`),
  });
  const lead = useQuery<LeadTimeSettingsResponse>({ queryKey: ["lead-settings"], queryFn: () => apiGet("/api/settings/lead-times") });
  const inSeason = (lead.data?.settings.season_months ?? []).includes(new Date().getMonth() + 1);

  const table = useMemo(() => (q.data ? trendTable(q.data.skus, dim, win, basis, q.data.orders) : null), [q.data, dim, win, basis]);
  const rows = useMemo(() => {
    if (!table) return [];
    const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
    const hit = (r: TrendRow) => !words.length || words.every((w) => `${r.value} ${r.skus.join(" ")}`.toLowerCase().includes(w));
    const out = table.rows.filter(hit);
    if (sort === "units") out.sort((a, b) => b.units - a.units);
    if (sort === "change") out.sort((a, b) => Math.abs(b.change_pct ?? 0) - Math.abs(a.change_pct ?? 0));
    return out;
  }, [table, filter, sort]);
  const drill = useMemo(() => {
    if (!q.data || !open || !table) return null;
    const row = table.rows.find((r) => r.value === open);
    if (!row) return null;
    const set = new Set(row.skus);
    return trendTable(q.data.skus.filter((s) => set.has(s.sku)), "sku", win, basis, q.data.orders);
  }, [q.data, open, table, win, basis]);
  const notStocked = useMemo(() => new Set(q.data?.skus.filter((s) => s.not_stocked).map((s) => s.sku)), [q.data]);

  const unit = isYards(type) ? "yd" : "units";
  const dimLabel = dims.find((d) => d.key === dim)?.label ?? dim;

  const exportRows = () =>
    table &&
    downloadCsv(`trends-${type}-${dim}-${win}w-${q.data?.end}.csv`, rows.map((r) => ({
      [dimLabel]: r.value,
      [`Last ${win} wks`]: Math.round(r.units),
      "Same wks last year": Math.round(r.units_last_year),
      "Change %": r.change_pct == null ? "" : Math.round(r.change_pct),
      "Share %": (r.share * 100).toFixed(1),
      "Share last year %": (r.share_last_year * 100).toFixed(1),
      "Share change pts": r.share_change_pts.toFixed(1),
      "Latest half vs earlier half %": r.momentum_pct == null ? "" : Math.round(r.momentum_pct),
      Orders: r.orders ?? "",
      Customers: r.customers ?? "",
      "Orders last year": r.orders_last_year ?? "",
      "Customers last year": r.customers_last_year ?? "",
      Parts: r.skus.length,
    })));

  return (
    <div className="u-page">
      <CategoryTabs
        items={TYPES}
        value={type}
        onChange={(t) => { setType(t); setOpen(null); }}
        label={(t) => CATEGORY_LABEL[t]}
        right={
          <>
            <div className="seg" role="group" aria-label="Group by">
              {dims.map((d) => (
                <button key={d.key} type="button" aria-pressed={dim === d.key} onClick={() => { setDimByType({ ...dimByType, [type]: d.key }); setOpen(null); }}>
                  {d.label}
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label="Window">
              {([4, 8] as const).map((w) => (
                <button key={w} type="button" aria-pressed={win === w} onClick={() => setWin(w)}>{w} weeks</button>
              ))}
            </div>
            <div className="seg" role="group" aria-label="Count">
              <button type="button" aria-pressed={basis === "umbrellas"} onClick={() => setBasisByType({ ...basisByType, [type]: "umbrellas" })} title="Only parts that went out inside finished umbrellas — customer choices, without spares">
                In umbrellas
              </button>
              <button type="button" aria-pressed={basis === "all"} onClick={() => setBasisByType({ ...basisByType, [type]: "all" })} title="Every unit used, including parts sold on their own">
                All use
              </button>
            </div>
          </>
        }
      />
      <p className="page-note">
        What customers are choosing, compared with the same weeks last year. Numbers only — no flags or advice; what they mean
        is your call. {q.data && <>Weeks to {shortDate(q.data.end)}; last year is the same weeks of {new Date(q.data.last_from).getFullYear()}.</>}
        {lead.data && !inSeason && <> <b>Off season:</b> volumes are low, so small numbers swing a lot.</>}
      </p>
      <ListBar
        title={`${CATEGORY_LABEL[type]} by ${dimLabel.toLowerCase()} · ${table ? fmt(table.total) : "…"} ${unit} in ${win} weeks`}
        search={filter}
        onSearch={setFilter}
        placeholder="Filter values or parts"
      >
        <ExportButton onClick={exportRows} disabled={!table} />
      </ListBar>

      {q.error ? (
        <div className="u-error">Couldn't load trends: {(q.error as Error).message}</div>
      ) : !table ? (
        <div className="tablewrap"><div className="u-empty">Loading…</div></div>
      ) : (
        <div className="tablewrap">
          <table className="dt left">
            <thead>
              <tr>
                <th>{dimLabel}</th>
                <SortTh k="units" sort={sort} setSort={setSort}>Last {win} weeks<br /><span className="sub">{unit}</span></SortTh>
                <th className="hide-m">Same weeks<br /><span className="sub">last year</span></th>
                <th className="hide-m">Orders · customers<br /><span className="sub">(last year)</span></th>
                <SortTh k="change" sort={sort} setSort={setSort} cls="hide-m">Change<br /><span className="sub">vs last year</span></SortTh>
                <th className="nb-h hide-m"><i className="stock" />Share<br /><span className="sub">of all {CATEGORY_LABEL[type].toLowerCase()} (last year = tick)</span></th>
                <SortTh k="share" sort={sort} setSort={setSort}>Share moved<br /><span className="sub">points vs last year</span></SortTh>
                <th className="hide-m">Recent<br /><span className="sub">last {win / 2} weeks vs {win / 2} before</span></th>
                <th className="l hide-m">{SERIES_WEEKS} weeks<br /><span className="sub">this year solid, last year dashed</span></th>
                <th className="hide-m" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td className="l muted" colSpan={10}>No sales in these weeks.</td></tr>}
              {rows.map((r) => (
                <Fragment key={r.value}>
                  <tr className={`click${open === r.value ? " sel" : ""}`} onClick={() => setOpen(open === r.value ? null : r.value)}>
                    <td className="l">
                      <span className="code">{r.value}</span>
                      <span className="sub"> · {r.skus.length} {r.skus.length === 1 ? "part" : "parts"}</span>
                    </td>
                    <Cells r={r} max={shareMax(table.rows)} />
                    <td className="chev hide-m">{open === r.value ? "⌄" : "›"}</td>
                  </tr>
                  {open === r.value &&
                    drill?.rows.map((p) => (
                      <tr key={p.value} className="drill">
                        <td className="l ind">
                          <span className="code">{p.value}</span>
                          {partName(p.value) && <span className="sub"> · {partName(p.value)}</span>}
                          {notStocked.has(p.value) && <span className="sub"> · not stocked</span>}
                        </td>
                        <Cells r={p} shareNote={`of ${r.value}`} max={shareMax(drill.rows)} />
                        <td className="hide-m" />
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="fine">
        "In umbrellas" counts parts that went out inside finished umbrellas — the frame, cover and colour customers picked.
        "All use" adds parts sold on their own (mostly spares). Share is of all {CATEGORY_LABEL[type].toLowerCase()} in the same
        weeks, so it isn't thrown off by the season. Each part is counted by its own code; names come from the codes, and a code
        that doesn't say (e.g. a frame with no finish number) is grouped as "Not in code". Orders · customers counts the
        distinct invoices and cash sales those units came from, and the distinct customers behind them — an order taking several
        parts of a group counts once — so a move made by many customers reads differently from one big order. Our own companies
        aren't counted as customers. Grey numbers rest on fewer than
        {" "}{SMALL} {unit}, so one order can swing them. Click a column heading to sort. March Products only.
      </p>
    </div>
  );
}

/** Below this many units a single order can swing a percentage, so it's shown in grey. Display only. */
const SMALL = 20;
const few = (n: number) => n < SMALL;
const SMALL_TITLE = `Fewer than ${SMALL} behind this number, so one order can swing it`;

/** The bar scale for a list: its biggest share, now or last year. */
const shareMax = (rows: TrendRow[]) => Math.max(0.01, ...rows.map((x) => Math.max(x.share, x.share_last_year)));

/** Share as a thin bar, left to right: solid = now, the tick = the same weeks last year. */
function ShareBar({ now, last, max }: { now: number; last: number; max: number }) {
  return (
    <span className="nb-track share-track" aria-hidden="true">
      {now > 0 && <i className="stock" style={{ width: `${Math.max(2, (now / max) * 100)}%` }} />}
      <b className="ly" style={{ left: `${Math.min(100, (last / max) * 100)}%` }} />
    </span>
  );
}

function Cells({ r, shareNote, max }: { r: TrendRow; shareNote?: string; max: number }) {
  const g = (small: boolean) => (small ? { className: "muted", title: SMALL_TITLE } : {});
  return (
    <>
      <td>{fmt(r.units)}</td>
      <td className="hide-m muted">{fmt(r.units_last_year)}</td>
      <td className="hide-m">
        {r.orders == null ? (
          <span className="muted">—</span>
        ) : (
          <>
            {fmt(r.orders)} · {fmt(r.customers ?? 0)}{" "}
            <span className="sub">({fmt(r.orders_last_year ?? 0)} · {fmt(r.customers_last_year ?? 0)})</span>
          </>
        )}
      </td>
      <td className="hide-m">{r.change_pct == null ? (r.units > 0 ? <span className="muted">new</span> : "—") : <span {...g(few(r.units_last_year))}>{signed(r.change_pct)}</span>}</td>
      <td className="hide-m">
        {pctOf(r.share)} <span className="sub">({pctOf(r.share_last_year)})</span>
        {shareNote && <span className="sub"> {shareNote}</span>}
        <ShareBar now={r.share} last={r.share_last_year} max={max} />
      </td>
      <td>
        <span className="nowrap"><span {...g(few(r.units) && few(r.units_last_year))}>{signed(r.share_change_pts, 1, " pts")}</span></span>
        {/* Phones drop the Change and Share columns, so the share itself goes under the move. */}
        <span className="m-only sub">{Math.round(r.share * 100)}% (was {Math.round(r.share_last_year * 100)}%)</span>
      </td>
      <td className="hide-m"><span {...g(few(r.units))}>{signed(r.momentum_pct)}</span></td>
      <td className="l hide-m"><Spark now={r.weekly} last={r.weekly_last_year} /></td>
    </>
  );
}

/** A header that sorts the list (highest first); the active one shows an arrow. */
function SortTh({ k, sort, setSort, cls, children }: { k: Sort; sort: Sort; setSort: (s: Sort) => void; cls?: string; children: React.ReactNode }) {
  return (
    <th className={cls} aria-sort={sort === k ? "descending" : "none"}>
      <button type="button" className="th-sort" onClick={() => setSort(k)} title={`Sort by ${SORT_LABEL[k].toLowerCase()}`}>
        {children}{sort === k && <span className="arr"> ↓</span>}
      </button>
    </th>
  );
}

/** Weekly units: this year solid, last year dashed, on one scale. */
function Spark({ now, last }: { now: number[]; last: number[] }) {
  const w = 120;
  const h = 26;
  const max = Math.max(1, ...now, ...last);
  const pts = (xs: number[]) =>
    xs.map((v, i) => `${((i / Math.max(1, xs.length - 1)) * (w - 2) + 1).toFixed(1)},${(h - 1 - (v / max) * (h - 2)).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Weekly units, this year and last year">
      <polyline points={pts(last)} fill="none" stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 2" opacity="0.6" />
      <polyline points={pts(now)} fill="none" stroke="var(--brand)" strokeWidth="1.6" />
    </svg>
  );
}
